import store from "../../../../../store";
import { playJoinSound } from "../sound";
import { getCurrentAudioOutputDeviceId, setAudioOutputDeviceAsCurrent } from "../devices";
import RematchSocket from "../../../../../socket/rematchSocket";
import { togglePictureInPicture } from "../media";
import roomAPIService from "../../../roomAPIService";

const AuthStore = store.state.auth; // !!! AuthStore.user IS NOT AVAILABLE HERE, THAT IS WHY WE GET AUTHSTORE ONLY

let firstRemoteConnectDone = false;

/* Handle a RemoteParticipant already connected to the Room */
export async function handleExistingRemoteParticipants(room) {
  /* Subscribe to the media published by RemoteParticipants already in the Room. */
  room.participants.forEach(async (remoteParticipant) => {
    /* Setup handling for the RemoteParticipant's events and media changes when a remote user is found */
    if (remoteParticipant && remoteParticipant.identity !== AuthStore.user._id) {
      await handleRemoteParticipantMedia(remoteParticipant, room);
    }
  });
}

/* Handle future occasions of a RemoteParticipant joining the Room */
export async function handleFutureRemoteConnections(room) {
  /* Subscribe to the media published by RemoteParticipants joining the Room later. */
  room.on("participantConnected", async (remoteParticipant) => {
    /* Setup handling for the RemoteParticipant's events and media changes when
     * a remote user is found that is not the user itself. */
    if (remoteParticipant && remoteParticipant.identity !== AuthStore.user._id) {
      await handleRemoteParticipantMedia(remoteParticipant);
    }
  });
}

/**
 * NB! READ THESE TWO BELOW TO UNDERSTAND THE ORDER OF EVENT FIRINGS
 * https://github.com/twilio/twilio-video.js/issues/391
 *
 * THIS ONE ESPECIALLY; CHANGELOG THAT HELPS AVOID FOLLOWING OUTDATED INFORMATION.
 * https://github.com/twilio/twilio-video.js/blob/1.13.1/CHANGELOG.md#1100-may-30-2018
 *
 */
async function handleRemoteParticipantMedia(remoteParticipant) {
  console.log("remoteParticipant id: ", remoteParticipant.identity);
  await store.dispatch("sessionState/reset");
  if (firstRemoteConnectDone) {
    await store.dispatch("sessionState/setPartnerState", "partnerConnected");
  } else {
    firstRemoteConnectDone = true;
  }

  /* Play sound when RemoteParticipant joins */
  playJoinSound();

  /* When a partner joins, cancel existing rematch request if exists */
  const rematchRequestDoc = store.state.rematching.rematchRequestDoc;
  if (remoteParticipant && rematchRequestDoc) {
    const SID = store.state.session.sessionID;
    const result = await RematchSocket.cancelOneRematchRequest(SID, AuthStore.user._id);
    if (result.success) {
      store.dispatch("rematching/setRematchRequestDoc", null);
    }
  }

  await initRemoteParticipantAndListeners(remoteParticipant);
}

async function initRemoteParticipantAndListeners(remoteParticipant) {
  await store.dispatch("room/setRemoteParticipantData", remoteParticipant);

  console.log("REMOTE PARTICIPANT!!!!!", remoteParticipant);

  /** If number of tracks inside remoteparticipant tracks is bigger than 1 */
  if (!remoteParticipant.tracks || remoteParticipant.tracks.size < 1) {
    await store.dispatch("room/setRemoteStatesReady");
  }

  /** If remote participant has no video track, set audio track to false,
   * this is to ensure that when a user joins a room, the video icon is not shown
   * as turned on, when it is not.*/
  if (!remoteParticipant.videoTracks || remoteParticipant.videoTracks.size < 1) {
    await store.dispatch("room/setRemotePartnerMediaFalse", "partnerVideo");
  }

  /** If remote participant has no audio track, set audio track to false,
   * this is to ensure that when a user joins a room, the audio icon is not shown
   * as turned on, when it is not. */
  if (!remoteParticipant.audioTracks || remoteParticipant.audioTracks.size < 1) {
    await store.dispatch("room/setRemotePartnerMediaFalse", "partnerAudio");
  }

  /* Handle a RemoteParticipant publishing a track after they have connected to the Room */
  remoteParticipant.on("trackPublished", handlePublishedRemoteTrack);

  /* Handle a RemoteParticipant unpublishing a track after they have joined the room */
  remoteParticipant.on("trackUnpublished", handleUnpublishedRemoteTrack);

  /* Handle a RemoteParticipant reconnecting */
  remoteParticipant.on("reconnecting", handleReconnection);

  /* Handle a RemoteParticipant's successful reconnection */
  remoteParticipant.on("reconnected", handleSuccessfulReconnection);

  /* Handle a RemoteParticipant's disconnection */
  remoteParticipant.on("disconnected", handleRemoteDisconnection);

  /* Handle the TrackPublications already published by the Participant. */
  for (const remotePublication of remoteParticipant.tracks.values()) {
    await handlePublishedRemoteTrack(remotePublication);
  }

  /** Set partner states as initialized @NB MUST BE DONE LAST, otherwise
   * it will show a message of 'your partner will not see your camera until
   * they turn on their camera' right before their camera feed appears, which
   * is not a smooth UX and not true either, as at that moment, the camera is
   * not turned off, but not even available yet. ALSO, it needs to run whether
   * or not any tracks are used, as sometimes you can join the session and
   * both of the partner's tracks are turned off. */
  await store.dispatch("room/setRemoteStatesReady");
}

/* PUBLISHING */

/**
 * Handle to the TrackPublication's media.
 * @param remoteTrackPublication - the TrackPublication
 */
async function handlePublishedRemoteTrack(remoteTrackPublication) {
  console.log("@remoteParticipant published track", remoteTrackPublication);

  /* Detect if screen share, then don't attach nor listen to anything */
  if (remoteTrackPublication && remoteTrackPublication.track) {
    if (remoteTrackPublication.track.name && remoteTrackPublication.track.name == "screen") {
      return;
    }
    /* If the publication is subscribed to, attach its track to the DOM */
    if (remoteTrackPublication.isSubscribed) {
      await attachRemoteTrack(remoteTrackPublication.track, remoteTrackPublication.kind);
    }
  }

  /* LocalParticipant subscribed to the RemoteTrack. */
  remoteTrackPublication.on("subscribed", handleSubscribedRemoteTrack);

  /* LocalParticipant unsubscribed from the RemoteTrack. */
  remoteTrackPublication.on("unsubscribed", handleUnsubscribedRemoteTrack);

  /* LocalParticipant failed to subscribe to the RemoteTrack. */
  remoteTrackPublication.on("subscriptionFailed", handleRemoteTrackFailedSubscription);
}

function handleUnpublishedRemoteTrack(remoteTrackPublication) {
  console.log("@handleUnpublishedRemoteTrack, user probably stopped camera.", remoteTrackPublication, remoteTrackPublication.track);
  if (!remoteTrackPublication) {
    return console.warn("Sorry, this shouldn't happen!");
  }

  let remoteTrackName = remoteTrackPublication.trackName;
  if (remoteTrackName && remoteTrackName == "screen") {
    store.dispatch("room/setRemoteParticipantScreenData", null);
  }
}

/* SUBSCRIBING */

export async function handleSubscribedRemoteTrack(remoteTrack) {
  console.log("LocalParticipant subscribed to the RemoteTrack: ", remoteTrack);
  /* Attach RemoteParticipant's track to the DOM */
  await attachRemoteTrack(remoteTrack, remoteTrack.kind);
}

function handleUnsubscribedRemoteTrack(remoteTrack) {
  console.log("LocalParticipant unsubscribed from the RemoteTrack. ", remoteTrack);
  if (remoteTrack) {
    detachRemoteTrack(remoteTrack);
  }
}

async function handleRemoteTrackFailedSubscription(error) {
  console.log("LocalParticipant failed to subscribe to the RemoteTrack", error);
  await roomAPIService.logSessionError(error);
}

/* CONNECTION */

async function handleReconnection(remoteParticipant) {
  console.log("@remoteParticipant is reconnecting", remoteParticipant);
  /** If user has left, then ignore Twilio reconnection event. */
  if (store.state.sessionState.partnerLeft) return;

  await store.dispatch("sessionState/setPartnerState", "partnerReconnecting");
}

async function handleSuccessfulReconnection() {
  console.log("@remoteParticipant has reconnected.");
  await store.dispatch("sessionState/setPartnerState", "partnerConnected");

  let pictureInPictureEnabled = store.state.mediastates.userMediaSettings.pictureInPictureEnabled;
  if (pictureInPictureEnabled) {
    /** Can't remember why double right now, but there was a reason, so keeping it; */
    await togglePictureInPicture();
    await togglePictureInPicture();
  }
}

/* Handle when user has disconnected */
export async function handleRemoteDisconnection(remoteParticipant) {
  console.log("@remoteParticipant disconnected", remoteParticipant);
  await store.dispatch("sessionState/setPartnerState", "partnerDisconnected");
  /* await resetRemoteParticipant(remoteParticipant); */
}

export async function handleRemoteParticipantLeave() {
  await store.dispatch("sessionState/setPartnerState", "partnerLeft");
  await resetRemoteParticipant();
}

export function setFirstRemoteConnectFalse() {
  firstRemoteConnectDone = false;
}

/* ATTACHING & DETACHING TO DOM */

/**
 * Attach a RemoteTrack's Track to the DOM.
 * @param remoteTrack - the remoteTrack to attach
 */
export async function attachRemoteTrack(remoteTrack, kind) {
  console.log("RemoteParticipant track attach", remoteTrack, kind);
  if (!remoteTrack || !kind) return;

  /* Start listening to RemoteTrackPublication states */
  listenToRemoteTrackStateChanges(remoteTrack);

  if (remoteTrack.name && remoteTrack.name == "screen") {
    await store.dispatch("room/setRemoteParticipantScreenData", remoteTrack);
    let container = document.getElementById("remoteScreenshare");

    /** Replacing all children here to ensure only one screenshare gets attached,
     * can use this here as screenshare has its own dedicated container, meaning
     * there should only ever be one element inside it. */
    container.replaceChildren(remoteTrack.attach());
  } else {
    let container = document.getElementById("remote-video");

    /** Prevent double adding of video or audio */
    if (container.querySelector(kind) !== null) {
      return;
    }

    /* Handle custom audio output: https://www.twilio.com/docs/video/configuring-audio-video-input-and-output-devices */
    if (remoteTrack.kind == "audio") {
      const audioElement = remoteTrack.attach();
      if (!audioElement) return;
      const currentAudioOutputDeviceId = await getCurrentAudioOutputDeviceId();
      await audioElement.setSinkId(currentAudioOutputDeviceId);
      container.appendChild(audioElement);
      await store.dispatch("room/setRemoteAudioTrack", remoteTrack);
      await setAudioOutputDeviceAsCurrent(currentAudioOutputDeviceId);
    } else {
      container.appendChild(remoteTrack.attach());

      await store.dispatch("room/setRemoteStatesReady");

      /** In case of a race condition, RemoteParticipantData might not exist here yet
       * which caused many situations of one person not seeing the other. Here we have
       * another opportunity to set the RemoteParticipantData */
    }
  }
}

/**
 * Detach a Track from the DOM.
 * @param remoteTrack - the Track to be detached
 */
export function detachRemoteTrack(remoteTrack) {
  // Detach the Participant's Track from the thumbnail.
  console.log("RemoteParticipant track detach", remoteTrack);
  if (remoteTrack.kind !== "data") {
    setRemoteMediaStatus(remoteTrack, false);
    remoteTrack.detach().forEach((detachedElement) => {
      detachedElement.remove();
    });
    store.dispatch("room/setRemoteAudioTrack", null);
  }
}

function listenToRemoteTrackStateChanges(remoteTrack) {
  if (!remoteTrack) {
    return console.warn("Remote track missing");
  }
  /* Set initial state of track */
  let newStatus = remoteTrack.isEnabled;
  setRemoteMediaStatus(remoteTrack, newStatus);

  if (remoteTrack.kind === "audio") {
    remoteTrack.on("enabled", () => {
      console.log("RemoteParticipant changed media status to enabled");
      setRemoteMediaStatus(remoteTrack, true);
    });

    remoteTrack.on("disabled", () => {
      console.log("RemoteParticipant changed media status to disabled");
      setRemoteMediaStatus(remoteTrack, false);
    });
  }
}

function setRemoteMediaStatus(remoteTrack, newStatus) {
  let mediaType = "";
  if (remoteTrack && remoteTrack.name && remoteTrack.name == "screen") {
    mediaType = "partnerScreen";
  } else if (remoteTrack.kind === "video") {
    mediaType = "partnerVideo";
  } else if (remoteTrack.kind === "audio") {
    mediaType = "partnerAudio";
  }
  let data = {
    mediaType,
    newStatus,
  };
  store.dispatch("room/setRemoteMediaStatus", data);
}

async function resetRemoteParticipant(remoteParticipant = null) {
  if (!remoteParticipant) {
    remoteParticipant = store.state.room.remoteParticipantData;
  }

  if (remoteParticipant) {
    remoteParticipant.tracks.forEach(function (remoteTrackPublication) {
      if (remoteTrackPublication.track) {
        detachRemoteTrack(remoteTrackPublication.track);
      }
    });
  } else {
    /** ====== HARD DELETE IF STILL NO REMOTE FOUND====== */
    /* Remove all video and audio tracks from RemoteParticipant */
    const remoteVideoElement = document.getElementById("remote-video");
    if (remoteVideoElement) {
      while (remoteVideoElement.firstChild) {
        remoteVideoElement.firstChild.remove();
      }
    }

    /* Remove screenshare track from RemoteParticipant */
    const remoteScreenshareElement = document.getElementById("remoteScreenshare");
    if (remoteScreenshareElement) {
      while (remoteScreenshareElement.firstChild) {
        remoteScreenshareElement.firstChild.remove();
      }
    }
  }

  /** Reset states or possibly upon re-connection, one partner won't see the other */

  await store.dispatch("room/resetRemoteParticipantData");

  await store.dispatch("mediastates/resetPartnerStates");

  await store.dispatch("rematching/setNewPartner", null);
}

/* As all the event listeners are set on the RemoteParticipant
and its tracks, when the RemoteParticipant is replaced with the
new RemoteParticipant and the old tracks are replaced with the
new, then the reboot has been successful. */
export async function rebootRemoteParticipant(room) {
  /* Remove all video and audio tracks from RemoteParticipant */
  const remoteVideoElement = document.getElementById("remote-video");
  if (remoteVideoElement) {
    while (remoteVideoElement.firstChild) {
      remoteVideoElement.firstChild.remove();
    }
  }

  /* Remove screenshare track from RemoteParticipant */
  const remoteScreenshareElement = document.getElementById("remoteScreenshare");
  if (remoteScreenshareElement) {
    while (remoteScreenshareElement.firstChild) {
      remoteScreenshareElement.firstChild.remove();
    }
  }

  let remoteParticipant = null;
  room.participants.forEach((participant) => {
    if (participant && participant.identity !== AuthStore.user._id) {
      remoteParticipant = participant;
    }
  });

  if (remoteParticipant === null) return;

  /**@todo: Do we need to rematching/setNewPartner to null here? */

  /* Setup handling for the RemoteParticipant's events and media changes */
  await initRemoteParticipantAndListeners(remoteParticipant);

  /* Set if they were audio on or off, video on or off */
}

// function onParticipantUnpublishedTrack(track, trackPublication) {
//   console.log("trackPublication", trackPublication);
//   detachTracks([track]);
// }

// function getTracks(participant) {
//   return Array.from(participant.tracks.values())
//     .filter(function(publication) {
//       return publication.track;
//     })
//     .map(function(publication) {
//       return publication.track;
//     });
// }

// function detachParticipantTracks(participant) {
//   var tracks = getTracks(participant);
//   detachTracks(tracks);
// }

// function detachTracks(tracks) {
//   for (let track of tracks) {
//     const htmlElements = track.detach();
//     for (let htmlElement of htmlElements) {
//       htmlElement.remove();
//     }
//   }
// }

export default {
  handleExistingRemoteParticipants,
  handleFutureRemoteConnections,
  setFirstRemoteConnectFalse,
  rebootRemoteParticipant,
  handleRemoteParticipantLeave,
};
